import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Trash2, Pencil, ExternalLink, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { triggerGbpPost } from "@/lib/gbp.functions";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";


export const Route = createFileRoute("/admin/posts")({
  component: PostsListPage,
  head: () => ({ meta: [{ title: "Posts — Admin" }, { name: "robots", content: "noindex" }] }),
});

type Status = "all" | "draft" | "scheduled" | "published";

function PostsListPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>("all");
  const [search, setSearch] = useState("");

  const { data: posts, isLoading } = useQuery({
    queryKey: ["admin", "posts", status],
    queryFn: async () => {
      let q = supabase
        .from("posts")
        .select("id, title, slug, status, updated_at, published_at, scheduled_for, categories(name)")
        .order("updated_at", { ascending: false });
      if (status !== "all") q = q.eq("status", status);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  const filtered = (posts ?? []).filter((p) =>
    !search.trim() || p.title.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const onDelete = async (id: string) => {
    if (!confirm("Delete this post permanently?")) return;
    const { error } = await supabase.from("posts").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Post deleted");
    qc.invalidateQueries({ queryKey: ["admin", "posts"] });
  };

  const onTriggerGbp = async (id: string, title: string) => {
    const t = toast.loading(`Sending "${title}" to Google…`);
    try {
      await triggerGbpPost({ data: { postId: id } });
      toast.success("Sent to Google Business Profile", { id: t });
    } catch (err) {
      toast.error(`GBP sync failed: ${(err as Error).message}`, { id: t });
    }
  };


  return (
    <AdminLayout title="Posts">
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-slate-200">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search posts..." className="pl-9" />
          </div>
          <Select value={status} onValueChange={(v) => setStatus(v as Status)}>
            <SelectTrigger className="w-full sm:w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="draft">Drafts</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold hidden md:table-cell">Category</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold hidden lg:table-cell">Date</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr><td colSpan={5} className="text-center py-12 text-slate-500">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-slate-500">
                  No posts. <Link to="/admin/posts/new" className="text-sky-600 hover:underline">Create one</Link>
                </td></tr>
              ) : (
                filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to="/admin/posts/$id" params={{ id: p.id }} className="font-medium text-sky-700 hover:underline">
                        {p.title || "(no title)"}
                      </Link>
                      <div className="text-xs text-slate-400">/{p.slug}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-600">
                      {p.categories?.name ?? "—"}
                    </td>
                    <td className="px-4 py-3"><StatusPill status={p.status} /></td>
                    <td className="px-4 py-3 hidden lg:table-cell text-slate-500 text-xs">
                      {p.status === "scheduled" && p.scheduled_for
                        ? `Scheduled: ${format(new Date(p.scheduled_for), "PP p")}`
                        : p.published_at
                          ? `Published: ${format(new Date(p.published_at), "PP")}`
                          : `Updated: ${format(new Date(p.updated_at), "PP")}`}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {p.status === "published" && (
                          <>
                            <a href={`/articles/${p.slug}`} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-sky-600 p-1.5" title="View post">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                            <button
                              onClick={() => onTriggerGbp(p.id, p.title)}
                              className="text-slate-500 hover:text-emerald-600 p-1.5"
                              title="Trigger Google Post"
                            >
                              <Send className="h-4 w-4" />
                            </button>
                          </>
                        )}

                        <Link to="/admin/posts/$id" params={{ id: p.id }} className="text-slate-500 hover:text-sky-600 p-1.5">
                          <Pencil className="h-4 w-4" />
                        </Link>
                        <button onClick={() => onDelete(p.id)} className="text-slate-500 hover:text-red-600 p-1.5">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    published: "bg-green-100 text-green-700",
    scheduled: "bg-amber-100 text-amber-700",
    draft: "bg-slate-100 text-slate-600",
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${map[status] ?? ""}`}>{status}</span>;
}

export { Button };
